import { KeyValuePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { GmrDataService } from '../core/gmr-data.service';
import { UiStateService } from '../core/ui-state.service';
import { GmrFunction } from '../core/gmr.models';
import { DecodeEntitiesPipe } from '../shared/pipes/decode-entities.pipe';

//
// 1:1 port of the always-visible left sidebar (.menuLeft) in menu.html. Split out from
// function-picker.component.ts (which renders the filterable step1 list) because .menuLeft must
// be a true flex-sibling of .getMyReport within .appContent, matching the original flat markup -
// nesting it inside a component placed within #container (as one component doing both jobs did)
// broke that sibling relationship and pulled the sidebar in from the left edge, since it then
// inherited #background's centered 75vw width instead of spanning the full flex row.
//
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [KeyValuePipe, TranslateDirective, TranslatePipe, DecodeEntitiesPipe],
  //
  // .menuLeft's own `flex: 200px 0 0` (menu.css/site.css) only takes effect on a DIRECT child of
  // a flex container. <app-sidebar> is that direct child of .appContent now, not .menuLeft
  // itself, so the fixed-width flex-item sizing has to be declared on the host instead - display
  // stays block since this element's OWN content (.menuLeft) is normal block flow, independent of
  // how the host behaves as a flex item within its parent.
  //
  // min-width: 0 overrides the flex item's default automatic minimum size, which is otherwise the
  // content's intrinsic min-content width - a long, unbroken group heading (e.g. a lengthy
  // untranslated resource key) would otherwise force the host past its 200px flex-basis, even
  // though .reportGroupHeader h4 itself already truncates with an ellipsis once given the chance.
  //
  // At the same breakpoints where menu.css/site.css send .menuLeft off-canvas on mobile
  // (position:absolute; left:-800px, toggled via .show), the host's own 200px flex-basis must
  // collapse to 0 too - .menuLeft going off-canvas only removes ITS OWN box from the flex row; in
  // the original flat markup that was enough, but here .menuLeft is nested inside this component's
  // own flex-item host, which still reserves 200px in .appContent's row regardless of what its
  // absolutely-positioned child does. Left uncollapsed, .getMyReport was squeezed into
  // (viewport width - 200px) with the leftover 200px rendering as an empty gap on every mobile
  // screen, not just while the (off-canvas, hidden) menu happened to be closed.
  //
  styles:
  [
    ':host { display: block; flex: 200px 0 0; min-width: 0; height: 100%; }'
  , '@media only screen and (max-width: 1023px) and (orientation: landscape) { :host { flex: 0 0 0; width: 0; } }'
  , '@media only screen and (max-width: 767px) and (orientation: portrait) { :host { flex: 0 0 0; width: 0; } }'
  ],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent
{
  protected readonly data = inject(GmrDataService);
  protected readonly ui = inject(UiStateService);

  //
  // Which sidebar row is highlighted. Derived from the application's actual selection rather than
  // tracked locally, and that matters: this used to be a `signal('home')` written only by this
  // component's own selectFunction(). Every other route into a report therefore left the highlight
  // behind - clicking a report in the main step1 list, opening a bookmarked #preset link, or using
  // the browser's Back/Forward buttons - and 'home' stayed lit while a report was on screen.
  //
  // Reading selectedFunction() means there is one source of truth and no route can miss it.
  //
  protected readonly activeItem = computed(() =>
    this.ui.step() === 'parameterForm'
      ? this.data.selectedFunction()?.cfn_preset_name ?? 'home'
      : 'home');

  //
  // keyvalue's default sort is alphabetical by key - groupBy's Map preserves the original
  // first-seen insertion order (matching AngularJS' object-keyed ng-repeat).
  //
  protected readonly originalOrder = () => 0;

  //
  // Port of closeGroupHeading/closeGroupIcon for this list, with one deliberate behaviour change
  // from the original (which started every group expanded): groups start COLLAPSED here, tracked
  // as the set of groups the user has explicitly expanded rather than the set collapsed, so a
  // group key that was never toggled defaults to collapsed. Collapse state stays local to this
  // component, matching the original: collapsing a group in the sidebar never affected the same
  // group's collapse state in the step1 filtered list, since each was toggled via independent DOM
  // traversal from the clicked element.
  //
  private readonly expandedGroups = signal(new Set<string>());

  isGroupCollapsed(groupKey: string): boolean
  {
    return !this.expandedGroups().has(groupKey);
  }

  toggleGroupCollapse(groupKey: string): void
  {
    this.expandedGroups.update(current =>
    {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return next;
    });
  }

  selectFunction(fn: GmrFunction): void
  {
    const showsParameterForm = this.data.selectFunction(fn);
    if (showsParameterForm)
    {
      this.ui.goToParameterForm(fn.cfn_preset_name);
    }
  }
}
